import {
  Order,
  OrderDoc,
  OrderProduct,
  OrderStatusCode,
  ProductDoc,
  ProductStateCode,
  StoreDoc,
} from '@gooduncles/gu-app-schema';
import { message, notification } from 'antd';
import { Timestamp } from 'firebase/firestore';
import { useCallback, useState } from 'react';

import { formatNumber, parseIfObject } from 'src/utils/common';
import { AuditLogger, buildChanges } from 'src/utils/audit-logger';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';
import { getDeliveryRouteHistory, updateDeliveryTask, updatePickupTask } from 'src/utils/firebase/firebase-short-cut';
import { convertOrderProductsToTaskItems, getTasksForOrder } from 'src/utils/issue/task-util';
import { createOrderData, createOrderDataForModify, isTaskAssignedAceeptedOrder } from 'src/utils/order-util';

const auditLogger = AuditLogger.getInstance();
const firebaseManager = FirebaseManager.getInstance();
const orderPath = 'order';
const productPath = 'product';

const createOrderDoc = async (docData: Order) => {
  // 상품수량 및 상태체크
  try {
    const promises = docData.products.map((p) => firebaseManager.getDoc<ProductDoc>(`${productPath}/${p.productId}`));
    const products = await Promise.all(promises);
    const soldOutProducts = products.filter((p) => p?.state !== ProductStateCode.STOCK);
    if (soldOutProducts.length > 0) {
      throw new Error(`구매 불가능한 상품이 있습니다. ${soldOutProducts.map((p) => p?.fullName).join(', ')}`);
    }
  } catch (error: any) {
    throw new Error(error.message ?? '알 수 없는 에러발생!');
  }

  // 중복 제출(double-submit) 방지: 문서 ID를 `${storeId}_${deliveryDate}` 결정적 ID로 만들어
  // 동시 두 요청이 같은 문서 ref를 read/write 하도록 강제한다. auto-ID는 서로 다른 문서에 set 되어
  // 쓰기 충돌 없이 둘 다 커밋(중복)됐지만, 결정적 ID는 Firestore 낙관적 동시성이 작동해
  // 하나만 커밋되고 나머지는 재시도 시 가드에서 차단된다. (gu-commerce-web과 동일한 ID 스킴)
  const newOrderId = `${docData.storeId}_${docData.deliveryDate}`;

  // Pre-check: 전환기 안전장치. 구버전 클라가 만든 auto-ID SUBMITTED 주문은 결정적 ref로 잡히지 않으므로
  // storeId + SUBMITTED 쿼리로도 확인한다(query는 transaction.get()에서 미지원이라 트랜잭션 밖에서 조회).
  const legacySubmittedOrders = await firebaseManager.getDocsArrayWithWhere<OrderDoc>(orderPath, [
    ['storeId', '==', docData.storeId],
    ['orderStatus', '==', OrderStatusCode.SUBMITTED],
  ]);
  const legacySubmittedIds = legacySubmittedOrders.map((o) => o._id).filter((id) => id !== newOrderId);

  return firebaseManager.runTransaction(async (transaction) => {
    const newOrderRef = firebaseManager.docRef(`${orderPath}/${newOrderId}`);

    // 1. 결정적 ID 문서가 이미 활성/처리됨 상태인지 확인 (동시성 핵심 가드)
    // 동시 두 트랜잭션이 같은 ref를 읽으므로, 하나가 커밋하면 다른 하나는 재시도 시 여기서 차단된다.
    const sameSlotSnap = await transaction.get(newOrderRef);
    if (sameSlotSnap.exists()) {
      const sameSlotStatus = (sameSlotSnap.data() as OrderDoc | undefined)?.orderStatus;
      // CANCELED(60)/REJECTED(70) = 활성 주문 없음 → 같은 문서를 새 주문으로 재활용(덮어쓰기).
      // 그 외(SUBMITTED/ACCEPTED/DELIVERED) = 해당 배송일에 이미 유효 주문 존재 → 차단.
      const isReusable = sameSlotStatus === OrderStatusCode.CANCELED || sameSlotStatus === OrderStatusCode.REJECTED;
      if (!isReusable) {
        throw new Error('이미 제출된 주문이 있습니다.');
      }
    }

    // 2. (전환기) Pre-check에서 발견된 legacy auto-ID SUBMITTED 주문을 트랜잭션 내에서 재확인
    const legacySnaps = await Promise.all(
      legacySubmittedIds.map((id) => transaction.get(firebaseManager.docRef(`${orderPath}/${id}`))),
    );
    for (const snap of legacySnaps) {
      if ((snap.data() as OrderDoc | undefined)?.orderStatus === OrderStatusCode.SUBMITTED) {
        throw new Error('이미 제출된 주문이 있습니다.');
      }
    }

    // 3. 결정적 ref에 set. createDoc과 동일하게 _id/_timeCreate 메타데이터를 채운다.
    // (audit은 onCreateOrder에서 수동 로깅하므로 여기선 생략 — 기존 createDoc audit suppress와 동일)
    transaction.set(newOrderRef, { ...docData, _id: newOrderId, _timeCreate: Timestamp.now() });

    return newOrderId;
  });
};
const updateOrderDoc = async (orderId: string, docData: Partial<Order>) =>
  firebaseManager.updateDoc(orderPath, orderId, docData, { audit: { suppress: true } });
const cancelOrderDoc = async (orderId: string) =>
  firebaseManager.updateDoc(
    orderPath,
    orderId,
    { orderStatus: OrderStatusCode.CANCELED },
    { audit: { suppress: true } },
  );

const useOrder = () => {
  const [isLoading, setIsLoading] = useState(false);

  const onCreateOrder = useCallback(async (storeManagerId: string, store: StoreDoc, products: OrderProduct[]) => {
    setIsLoading(true);
    try {
      // 1. 주문을 생성합니다.
      const orderData = await createOrderData(storeManagerId, store, products);
      const orderId = await createOrderDoc(orderData);
      auditLogger.log({
        action: 'CREATE',
        entity: 'order',
        entityId: orderId,
        summary: `주문 생성 - ${store.storeNickname}, ${formatNumber(orderData.grandTotal)}원`,
        metadata: { storeId: store._id, orderStatus: orderData.orderStatus, grandTotal: orderData.grandTotal },
      });
      notification.success({
        message: '주문을 생성했습니다.',
        description: `총 금액: ${formatNumber(orderData.grandTotal)}`,
      });
      // 2. 이미 배송 동선이 생성된 상태라면 경고 메시지를 띄웁니다.
      if (orderData.date) {
        const deliveryRouteHistory = await getDeliveryRouteHistory(orderData.date);
        if (deliveryRouteHistory) {
          notification.warning({
            message: '이미 생성된 배송 동선이 있습니다.',
            description: '새로 생성한 주문을 파트너스에게 배정하려면 배송 동선 확정을 다시 해야합니다.',
            duration: 0,
          });
        }
      }

      setIsLoading(false);
      return true;
    } catch (error: any) {
      auditLogger.log({
        action: 'UPDATE',
        entity: 'order',
        entityId: 'unknown',
        summary: `주문 생성 실패 - ${parseIfObject(error)}`,
        level: 'error',
      });
      notification.error({
        message: '주문 생성 실패',
        description: error.message ?? '알 수 없는 에러발생!',
      });
    }
    setIsLoading(false);
    return false;
  }, []);

  const onUpdateOrderDoc = useCallback(
    async (store: StoreDoc, matchedOrder: OrderDoc, orderProducts: OrderProduct[]) => {
      setIsLoading(true);
      try {
        const beforeGrandTotal = matchedOrder.grandTotal;
        const orderData = await createOrderDataForModify(store, matchedOrder, orderProducts);
        // 1. 주문을 변경합니다.
        await updateOrderDoc(matchedOrder._id, orderData);
        auditLogger.log({
          action: 'UPDATE',
          entity: 'order',
          entityId: matchedOrder._id,
          summary: `주문 수정 - ${store.storeNickname}, ${formatNumber(beforeGrandTotal)}원 → ${formatNumber(orderData.grandTotal)}원`,
          changes: buildChanges(
            { grandTotal: beforeGrandTotal, products: matchedOrder.products },
            { grandTotal: orderData.grandTotal, products: orderData.products },
          ),
          metadata: { storeId: store._id, orderStatus: matchedOrder.orderStatus },
        });
        notification.success({
          message: '주문을 수정했습니다.',
          description: `총 금액: ${formatNumber(beforeGrandTotal)} -> ${formatNumber(orderData.grandTotal)}`,
        });

        // 2. 물류 업무 변경이 필요한 경우 함께 변경합니다.
        const needTaskChange = isTaskAssignedAceeptedOrder(matchedOrder);
        if (needTaskChange) {
          const { pickupTask, deliveryTask } = await getTasksForOrder(matchedOrder);
          const taskItems = convertOrderProductsToTaskItems(orderProducts);
          if (pickupTask) {
            await updatePickupTask(pickupTask._id, { pickupItems: taskItems });
            message.success('연관된 픽업 업무를 변경했습니다.');
          }
          if (deliveryTask) {
            await updateDeliveryTask(deliveryTask._id, { deliveryItems: taskItems });
            message.success('연관된 배송 업무를 변경했습니다.');
          }
        }
      } catch (error: any) {
        auditLogger.log({
          action: 'UPDATE',
          entity: 'order',
          entityId: matchedOrder._id,
          summary: `주문 수정 실패 - ${parseIfObject(error)}`,
          level: 'error',
        });
      }
      setIsLoading(false);
    },
    [],
  );

  const cancelOrder = useCallback(async (orderId: string) => {
    setIsLoading(true);
    try {
      await cancelOrderDoc(orderId);
      auditLogger.log({
        action: 'STATUS_CHANGE',
        entity: 'order',
        entityId: orderId,
        summary: `주문 취소`,
        changes: [{ field: 'orderStatus', before: undefined, after: 'CANCELED' }],
      });
    } catch (error: any) {
      auditLogger.log({
        action: 'UPDATE',
        entity: 'order',
        entityId: orderId,
        summary: `주문 취소 실패 - ${parseIfObject(error)}`,
        level: 'error',
      });
    }
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    onCreateOrder,
    onUpdateOrderDoc,
    cancelOrder,
  };
};

export default useOrder;
